import { flip, size } from '@floating-ui/react-dom'
import { Unstable_Popup as Popup } from '@mui/base/Unstable_Popup'
import { MenuItem } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import { Button, ButtonGroup, ButtonProps } from 'opensolar-ui'
import * as React from 'react'
import { ReactNode, useEffect } from 'react'
import { ChevronDownOutlineIcon } from '../icons'

// taken from https://github.com/mui/material-ui/blob/v5.15.18/docs/data/material/components/button-group/SplitButton.tsx

export type SplitButtonProps<ValueType, ItemType extends SplitButtonOption<ValueType>> = {
  // Can contain data objects, or random react components
  options: (ItemType | ReactNode)[]

  // Determines which option is shown in the Button
  selectedIndex?: number

  // Callback gets called when an item is clicked
  // Only gets called for data items, not react components
  onClick?: (value: ItemType, index: number) => void

  // the prop to use for the option's key, if not the index
  optionKey?: string

  // the prop to use for the option's disabled state
  optionDisabled?: string

  // whether the selected item should be shown in the menu
  showSelected?: boolean

  // whether the selection should be updated internally when items are clicked
  updateSelected?: boolean

  // whether the avoid closing the menu when an item is clicked
  dontClose?: boolean

  disabled?: ButtonProps['disabled']
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
  color?: ButtonProps['color']

  /** Placement of the dropdown relative to the button. e.g. 'top' to always show above. Default 'bottom'. */
  placement?: 'top' | 'bottom' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'

  style?: React.CSSProperties

  mainButtonHoverColor?: string
}

export type SplitButtonOption<ValueType> = {
  label: ReactNode
  icon?: ReactNode
  value?: ValueType
  onClick?: () => void
}

export const SplitButton = <ValueType, ItemType extends SplitButtonOption<ValueType>>({
  options,
  selectedIndex = 0,
  optionKey,
  optionDisabled,
  onClick,
  showSelected,
  updateSelected,
  dontClose,
  size,
  variant,
  color,
  disabled,
  placement = 'bottom',
  style,
  mainButtonHoverColor,
}: SplitButtonProps<ValueType, ItemType>) => {
  const [open, setOpen] = React.useState(false)
  const [isSplitButtonHovered, setIsSplitButtonHovered] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const [realSelected, setRealSelected] = React.useState(selectedIndex)

  const buttonGroupProps = {
    disabled,
    size,
    variant: !variant ? 'contained' : variant,
    color: color === 'default' ? 'secondary' : color,
    style,
  }

  const buttonProps = {
    disabled,
    size,
    variant,
    color,
  }
  const chevronHoverColor = mainButtonHoverColor
  const chevronColor = !disabled && isSplitButtonHovered && chevronHoverColor ? chevronHoverColor : 'currentColor'

  const handleClickMain = () => {
    setOpen(false)
    const item = options[selectedIndex] as ItemType
    if (onClick) onClick(item, selectedIndex)
    if (item.onClick) item.onClick()
  }

  useEffect(() => setRealSelected(selectedIndex), [selectedIndex])

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    if (updateSelected) setRealSelected(index)
    const item = options[index] as ItemType
    if (onClick) onClick(item, index)
    if (item.onClick) item.onClick()
    if (!dontClose) setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const renderOption = (index: number, isButton: boolean, props?: ButtonProps) => {
    const item = options[index]

    if (React.isValidElement(item)) {
      return item
    } else if (item && typeof item === 'object' && item['label']) {
      const optionDisabledState = optionDisabled ? (item as Record<string, unknown>)[optionDisabled] : false
      if (isButton)
        return (
          <Button
            {...(props || {})}
            disabled={buttonProps.disabled || !!optionDisabledState}
            onClick={handleClickMain}
            startIcon={item['icon']}
            sx={
              mainButtonHoverColor
                ? {
                    '&:hover, &:hover *': {
                      color: `${mainButtonHoverColor} !important`,
                    },
                  }
                : undefined
            }
          >
            {item['label']}
          </Button>
        )
      else {
        const key = optionKey ? item[optionKey] : index
        return (
          <MenuItem
            key={key}
            disabled={optionDisabled ? item[optionDisabled] : false}
            selected={index === realSelected}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            {item['icon']} {item['label']}
          </MenuItem>
        )
      }
    } else {
      console.log('item is not a valid element or object', item)
    }
  }

  const id = open ? 'simple-popper' : undefined

  return (
    <>
      {options.length === 1 ? (
        renderOption(0, true, buttonGroupProps)
      ) : (
        <ButtonGroup
          {...buttonGroupProps}
          color={color === 'default' ? 'secondary' : color}
          ref={anchorRef}
          aria-label="Button group with a nested menu"
          onMouseEnter={() => setIsSplitButtonHovered(true)}
          onMouseLeave={() => setIsSplitButtonHovered(false)}
        >
          {renderOption(realSelected, true, buttonProps)}
          <Button
            {...buttonProps}
            aria-describedby={id}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ChevronDownOutlineIcon strokeWidth={1.25} color={chevronColor} />
          </Button>
        </ButtonGroup>
      )}
      <Popup
        id={id}
        anchor={anchorRef.current}
        open={open}
        placement={placement}
        middleware={popupMiddleware}
        slotProps={{ root: { style: { zIndex: 2001 } } }}
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList id="split-button-menu">
              {options.map((option, index) => {
                if (!showSelected && index === realSelected) return null
                return renderOption(index, false)
              })}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popup>
    </>
  )
}

const popupMiddleware = [
  size({
    apply({ rects, elements }) {
      Object.assign(elements.floating.style, {
        minWidth: `${rects.reference.width}px`,
      })
    },
  }),

  // Flips the dropdown from the bottom to the top if there is not enough space
  flip(),
]
